import React, { useCallback, useState } from "react";
import * as Styled from "./styles";
import ReviewProvider from "./Components/Review";
import TabContentRenderer from "./Components/TabContentRenderer";
import ProviderConfigActions from "./Components/ProviderConfigActions";
import ProviderModals from "./Components/ProviderModals";
import {
  OnlinePaymentsConfigProvider,
  useOnlinePaymentsConfig,
} from "./Context/ValidationContext";
import { useProviderSubmission } from "./hooks/useProviderSubmission";
import { useProviderModals } from "./hooks/useProviderModals";
import { useProviderStatus } from "./hooks/useProviderStatus";
import { useProviderReview } from "./hooks/useProviderReview";
import type { ConfigMode } from "./Context/types";
import type { ExistingProvider, AvailableProvider } from "./types";
import type { Provider } from "../../../DealerAcquirersTable/types";

export type { ExistingProvider } from "./types";

export interface OnlinePaymentsConfigContentProps {
  mode?: ConfigMode;
  existingProvider?: ExistingProvider | null;
  onSuccess?: (
    provider?: Provider,
    meta?: { additionalStepPresented?: boolean },
  ) => void;
  onCancel?: () => void;
  onReviewModeChange?: (isReview: boolean) => void;
  onEditClick?: () => void;
  onProviderDeleted?: (providerId: string) => void;
  onProviderStatusChanged?: (providerId?: string, newState?: string) => void;
  onProviderUpdated?: (provider?: Provider) => void;
  merchantInfo?: { categoryCode: string } | null;
  availableProviders?: AvailableProvider[];
  dealerProviders?: Provider[];
  readOnly?: boolean;
}

interface OnlinePaymentsConfigContentInternalProps {
  onSuccess?: (
    provider?: Provider,
    meta?: { additionalStepPresented?: boolean },
  ) => void;
  onCancel?: () => void;
  onReviewModeChange?: (isReview: boolean) => void;
  onEditClick?: () => void;
  onProviderDeleted?: (providerId: string) => void;
  onProviderStatusChanged?: (providerId?: string, newState?: string) => void;
  onProviderUpdated?: (provider?: Provider) => void;
  dealerProviders?: Provider[];
}

function OnlinePaymentsConfigContent({
  onSuccess,
  onCancel,
  onReviewModeChange,
  onEditClick,
  onProviderDeleted,
  onProviderStatusChanged,
  onProviderUpdated,
  dealerProviders,
}: OnlinePaymentsConfigContentInternalProps) {
  const {
    mode,
    setIsLoading,
    existingProvider,
    setExistingProvider,
    internalReadOnly,
    setInternalReadOnly,
    dealerId,
    setFormErrors,
    selectedProvider,
    getValues,
    handleSubmit,
    clearErrors,
    watch,
    mcc,
  } = useOnlinePaymentsConfig();

  const [showReview, setShowReview] = useState(false);

  const review = useProviderReview({
    selectedProvider,
    watch,
  });

  const { getReviewProviderData, handleReviewToEdit: reviewToEdit } = review;

  const handleReviewToEdit = useCallback(() => {
    setShowReview(false);
    reviewToEdit();
  }, [reviewToEdit]);

  const modals = useProviderModals({
    dealerId,
    existingProvider,
    onProviderDeleted,
    onSuccess: () => onSuccess?.(),
  });

  const handleProviderCreated = useCallback(
    (provider?: Provider, meta?: { additionalStepPresented?: boolean }) => {
      onSuccess?.(provider, meta);
    },
    [onSuccess],
  );

  const { handleStatusChange } = useProviderStatus({
    mode,
    dealerId,
    existingProvider,
    getValues,
    setExistingProvider,
    onProviderStatusChanged,
  });

  const submission = useProviderSubmission({
    config: { mode, dealerId, dealerProviders },
    providers: { selectedProvider, existingProvider },
    form: { getValues, handleSubmit, setFormErrors, clearErrors },
    review: { showReview, setShowReview },
    modals: {
      setShowConfirmSave: modals.setShowConfirmSave,
      setShowSuccessMessage: modals.setShowSuccessMessage,
      setAdditionalStep: modals.setAdditionalStep,
      setIsLoading,
    },
    callbacks: {
      onReviewModeChange,
      onSuccess: handleProviderCreated,
      onProviderUpdated,
    },
  });

  const { isSaving, handlePrimaryAction, handleConfirmSave } = submission;

  const handleEdit = useCallback(() => {
    setInternalReadOnly(false);
    onEditClick?.();
  }, [onEditClick, setInternalReadOnly]);

  const handleCancelProvider = useCallback(() => {
    modals.handleCancelProvider();
  }, [modals]);

  const handleEditOrReviewToEdit = showReview ? handleReviewToEdit : handleEdit;
  const primaryAction = internalReadOnly
    ? onCancel || (() => {})
    : handlePrimaryAction;

  return (
    <Styled.Container>
      {!showReview && (
        <TabContentRenderer handleStatusChange={handleStatusChange} />
      )}
      {showReview && (
        <ReviewProvider
          provider={getReviewProviderData()!}
          mcc={mcc?.toString() || ""}
          selectedProvider={selectedProvider}
        />
      )}
      <Styled.DividerStyled />
      <ProviderConfigActions
        onEdit={handleEditOrReviewToEdit}
        onCancel={handleCancelProvider}
        onDelete={modals.handleDeleteProvider}
        onPrimaryAction={primaryAction}
        showReview={showReview}
      />
      <ProviderModals
        mode={mode}
        showCancelProvider={modals.showCancelProvider}
        showValidationError={modals.showValidationError}
        showDeleteProvider={modals.showDeleteProvider}
        showSuccessMessage={modals.showSuccessMessage}
        showConfirmSave={modals.showConfirmSave}
        additionalStep={modals.additionalStep}
        isDeleting={modals.isDeleting}
        isSaving={isSaving}
        onCancelProvider={modals.handleCancelProvider}
        onCloseValidationError={modals.handleCloseValidationError}
        onCloseDeleteModal={modals.handleCloseDeleteModal}
        onCloseSuccessMessage={modals.handleCloseSuccessMessage}
        onCloseConfirmSave={() => modals.handleCloseConfirmSave(isSaving)}
        onConfirmDelete={modals.handleConfirmDelete}
        onConfirmSave={handleConfirmSave}
        onCloseAdditionalStepModal={modals.handleCloseAdditionalStepModal}
        onCancel={onCancel}
      />
    </Styled.Container>
  );
}

const OnlinePaymentsConfig: React.FC<OnlinePaymentsConfigContentProps> = (
  props,
) => {
  const {
    mode,
    existingProvider,
    merchantInfo,
    availableProviders,
    dealerProviders,
    readOnly,
    ...restProps
  } = props;

  return (
    <OnlinePaymentsConfigProvider
      initialMode={mode}
      initialProvider={existingProvider}
      merchantInfo={merchantInfo}
      availableProviders={availableProviders}
      dealerProviders={dealerProviders}
      readOnly={readOnly}
    >
      <OnlinePaymentsConfigContent
        dealerProviders={dealerProviders}
        {...restProps}
      />
    </OnlinePaymentsConfigProvider>
  );
};

const AddOnlinePaymentsConfig: React.FC = () => {
  return <OnlinePaymentsConfig mode="ADD" />;
};

export default OnlinePaymentsConfig;
export { AddOnlinePaymentsConfig };
